import React, { useState, useMemo } from "react";
import {
  Database,
  CloudLightning,
  Shield,
  Zap,
  ArrowRight,
  RefreshCw,
  Layers,
  Target,
  Maximize,
} from "lucide-react";
import logo from "../../../assets/falcon.png";
import Header from "./Header";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../utils/auth-config";

/**
 * Deterministic pseudo-random number generator using the mulberry32 algorithm.
 * Returns a seeded function that produces stable, reproducible values in [0, 1).
 * This avoids the use of Math.random(), which SonarQube flags as a security concern
 * (sonar rule: javascript:S2245 — "Pseudorandom number generators should not be used
 * in secure contexts").
 *
 * @param {number} seed - A fixed integer seed for reproducibility.
 * @returns {() => number} A function that returns the next pseudo-random float.
 */
const createSeededRandom = (seed) => {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export default function Falcon360() {
  const { instance } = useMsal();
  const [activeFeature, setActiveFeature] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);
  const [activeSection, setActiveSection] = useState("overview");

  // Constants for configuration
  const PARTICLE_COUNT = 100;
  const PARTICLE_MAX_SIZE = 5;
  const PARTICLE_MIN_SIZE = 2;
  const PARTICLE_MAX_DURATION = 20;
  const PARTICLE_MIN_DURATION = 10;
  const PARTICLE_MAX_DELAY = 10;

  const ICON_SIZE = 32;
  const ICON_SMALL_SIZE = 24;

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error("Login redirect failed:", error);
    });
  };

  const keyFeatures = [
    {
      id: "feature-1",
      icon: <CloudLightning className="text-blue-400" size={ICON_SIZE} />,
      title: "Automated Data Mapping & Transformation",
      description:
        "Intelligently aligns data to fit target schema, saving time and reducing human error",
    },
    {
      id: "feature-2",
      icon: <Layers className="text-purple-400" size={ICON_SIZE} />,
      title: "Multi-Platform Compatibility",
      description: "Seamless migrations across SAP, Oracle, Bullhorn, and more",
    },
    {
      id: "feature-3",
      icon: <Shield className="text-green-400" size={ICON_SIZE} />,
      title: "Data Integrity and Security",
      description:
        "Strict controls and validation checks to protect sensitive business information",
    },
    {
      id: "feature-4",
      icon: <RefreshCw className="text-teal-400" size={ICON_SIZE} />,
      title: "Comprehensive Migration Management",
      description: "Full spectrum support from data extraction to validation",
    },
  ];

  const keyBenefits = [
    {
      id: "benefit-1",
      icon: <Zap className="text-yellow-400" size={ICON_SIZE} />,
      title: "Efficiency",
      description:
        "Streamlines the data migration process, reducing the complexity of managing multiple systems",
    },
    {
      id: "benefit-2",
      icon: <Target className="text-red-400" size={ICON_SIZE} />,
      title: "Precision",
      description:
        "Ensures accurate data mapping and transformation to maintain consistency",
    },
    {
      id: "benefit-3",
      icon: <Maximize className="text-emerald-400" size={ICON_SIZE} />,
      title: "Scalability",
      description:
        "Adaptable for migrations from single datasets to entire enterprise systems",
    },
  ];

  const navigationTabs = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "features", label: "Key Features" },
      { id: "benefits", label: "Benefits" },
    ],
    []
  );

  const benefitItems = useMemo(
    () => [
      {
        id: "item-1",
        title: "Fast and Reliable",
        description:
          "Reduce migration timelines with optimized processes, ensuring faster execution with minimal business disruptions.",
      },
      {
        id: "item-2",
        title: "Scalable for All Needs",
        description:
          "Designed to handle migrations from small-scale transfers to enterprise-level data migrations.",
      },
      {
        id: "item-3",
        title: "End-to-End Support",
        description:
          "Comprehensive support from initial planning to post-migration analysis, ensuring a smooth transition.",
      },
    ],
    []
  );

  /**
   * Particle styles are generated using a seeded PRNG (mulberry32) instead of Math.random().
   * Using a fixed seed (42) ensures:
   *  - Deterministic output on every render (no hydration mismatches in SSR).
   *  - SonarQube compliance: Math.random() (S2245) is no longer used.
   *  - Visually identical particle layout across sessions.
   */
  const particleStyles = useMemo(() => {
    const rand = createSeededRandom(42);
    return Array.from({ length: PARTICLE_COUNT }).map(() => ({
      left: `${rand() * 100}%`,
      top: `${rand() * 100}%`,
      width: `${rand() * PARTICLE_MAX_SIZE + PARTICLE_MIN_SIZE}px`,
      height: `${rand() * PARTICLE_MAX_SIZE + PARTICLE_MIN_SIZE}px`,
      opacity: rand() * 0.5,
      animation: `float ${
        rand() * PARTICLE_MAX_DURATION + PARTICLE_MIN_DURATION
      }s linear infinite`,
      animationDelay: `${rand() * PARTICLE_MAX_DELAY}s`,
    }));
  }, []);

  return (
    <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
      <div
        className="relative bg-gradient-to-b from-transparent via-blue-900 to-blue-900 min-h-screen text-white overflow-hidden"
        style={{ backgroundColor: "rgba(58, 89, 209, 0.8)" }}
      >
        {/* Animated Background Particles */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {particleStyles.map((style, i) => (
            <div
              key={`particle-${i}`}
              className="absolute rounded-full bg-blue-500"
              style={style}
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-4 py-20 relative">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block relative mb-6">
              <div className="center">
                <img
                  src={logo}
                  alt="Falcon Logo"
                  className="w-[250px] h-[250px] object-contain"
                />
              </div>
            </div>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              Seamless Data Migrations with BlueGecko's GenAI Engine
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex justify-center mb-12">
            {navigationTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`px-6 py-3 mx-2 rounded-lg transition-all duration-300
                  ${
                    activeSection === tab.id
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "bg-gray-800 text-blue-200 hover:bg-gray-700"
                  }`}
                onClick={() => setActiveSection(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Sections */}
          {activeSection === "overview" && (
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-blue-500 border-opacity-30">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
                Accelerate Data Understanding for Your Teams
              </h2>
              <p className="text-blue-200 mb-4">
                BlueGecko understands that data preparation can be
                time-consuming for Data Stewards, Data Owners, System
                Architects, Business Controllers, and Data Analysts. Our GenAI
                Engine is designed to make your data operations smoother and
                more efficient.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Zap
                    className="text-blue-400 mr-3"
                    size={ICON_SMALL_SIZE}
                  />
                  <span className="text-blue-200">
                    Automated Data Analysis: Uncover patterns and insights
                    across large volumes of legacy data
                  </span>
                </div>
                <div className="flex items-center">
                  <Database
                    className="text-purple-400 mr-3"
                    size={ICON_SMALL_SIZE}
                  />
                  <span className="text-blue-200">
                    Intelligent Data Categorization: Automate classification
                    based on content, context, and metadata
                  </span>
                </div>
                <div className="flex items-center">
                  <Shield
                    className="text-green-400 mr-3"
                    size={ICON_SMALL_SIZE}
                  />
                  <span className="text-blue-200">
                    Enhanced Data Documentation: Generate comprehensive data
                    dictionaries and lineage guidelines
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeSection === "features" && (
            <div className="grid md:grid-cols-2 gap-6">
              {keyFeatures.map((feature) => (
                <div
                  key={feature.id}
                  role="button"
                  tabIndex={0}
                  className={`p-6 rounded-2xl transition-all duration-300 cursor-pointer
                    ${
                      activeFeature === feature.id
                        ? "bg-gray-700 border-2 border-blue-500 shadow-2xl"
                        : "bg-gray-800 bg-opacity-50 border border-transparent"
                    }`}
                  onMouseEnter={() => setActiveFeature(feature.id)}
                  onMouseLeave={() => setActiveFeature(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      setActiveFeature(feature.id);
                    }
                  }}
                >
                  <div className="flex items-center mb-4">
                    {feature.icon}
                    <h3 className="ml-4 text-xl font-semibold text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-blue-200">{feature.description}</p>
                </div>
              ))}
            </div>
          )}

          {activeSection === "benefits" && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center">
                Why Choose Falcon Mapping?
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mb-10">
                {benefitItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-gray-800 bg-opacity-50 border border-transparent p-6 rounded-xl"
                  >
                    <h3 className="text-xl font-semibold text-blue-300 mb-4">
                      {item.title}
                    </h3>
                    <p className="text-blue-200">{item.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent text-center">
                  Benefits
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {keyBenefits.map((benefit) => (
                    <div
                      key={benefit.id}
                      role="button"
                      tabIndex={0}
                      className={`p-6 rounded-2xl transition-all duration-300 cursor-pointer
                        ${
                          activeBenefit === benefit.id
                            ? "bg-gray-700 border-2 border-blue-500 shadow-2xl"
                            : "bg-gray-800 bg-opacity-50 border border-transparent"
                        }`}
                      onMouseEnter={() => setActiveBenefit(benefit.id)}
                      onMouseLeave={() => setActiveBenefit(null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setActiveBenefit(benefit.id);
                        }
                      }}
                    >
                      <div className="flex items-center mb-4">
                        {benefit.icon}
                        <h4 className="ml-4 text-xl font-semibold text-white">
                          {benefit.title}
                        </h4>
                      </div>
                      <p className="text-blue-200">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-300 to-purple-300 bg-clip-text text-transparent">
              Start Your Migration Journey Today
            </h3>
            <p className="text-blue-200 max-w-2xl mx-auto mb-8">
              Ready to simplify your data migrations with Falcon Mapping? Let
              BlueGecko guide you through the process with powerful tools and a
              seamless migration experience.
            </p>
            <button
              type="button"
              onClick={handleLogin}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group"
            >
              Contact Us & Request a Demo
              <ArrowRight
                className="ml-3 group-hover:translate-x-2 transition-transform"
                size={20}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}